var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { create } from "zustand";
import { sendAjaxRequest } from "../API";
import { checkForSessionExpiry } from "../util/DataUtils";
const LOGPREFIX = "NotifStore: ";
export const queryNotifications = (state, auth, cacheBusterStore, callback, logger) => __awaiter(void 0, void 0, void 0, function* () {
    logger.log(LOGPREFIX + "queryNotifications");
    return new Promise((resolve, reject) => {
        if (!auth || !auth.userId || !auth.bearer) {
            logger.error(LOGPREFIX + "queryNotifications - user not signed in");
            reject(new Error("User is not signed in"));
            return;
        }
        try {
            const payload = {
                receiverUserId: auth.userId,
                sequence: auth.sequence,
                operation: "listNotifications",
                newerThan: 0, // TODO
            };
            logger.log(LOGPREFIX +
                "queryNotifications - ajax input = " +
                JSON.stringify(payload));
            sendAjaxRequest((ok, status, message) => {
                logger.log(LOGPREFIX +
                    "queryNotifications - result = " +
                    ok +
                    " " +
                    status +
                    " " +
                    message);
                try {
                    if (ok) {
                        const response = JSON.parse(message);
                        if (response.bearer && response.sequence) {
                            auth.setBearerAndSequence(response.bearer, response.sequence, logger);
                        }
                        if (callback) {
                            callback.handleUpdate({
                                ok: true,
                                loading: false,
                                done: true,
                                notifications: response.records,
                                load: state.load,
                                reset: state.reset,
                            });
                        }
                        logger.log(LOGPREFIX +
                            "queryNotifications - resolving to = " +
                            JSON.stringify(response));
                        resolve(response);
                        return;
                    }
                    else {
                        logger.error(LOGPREFIX + 'queryNotifications - message = "' + message + '"');
                        const response = JSON.parse(message);
                        checkForSessionExpiry(auth, response === null || response === void 0 ? void 0 : response.clientDetailCode, logger);
                        if (callback) {
                            callback.handleUpdate({
                                ok: false,
                                loading: false,
                                done: true,
                                error: response.error,
                                notifications: [],
                                load: state.load,
                                reset: state.reset,
                            });
                        }
                        reject(new Error("Unable to query notifications. " +
                            response.clientDetailCode +
                            " " +
                            response.error));
                        return;
                    }
                }
                catch (err) {
                    logger.error(LOGPREFIX + err);
                    if (callback) {
                        callback.handleUpdate({
                            ok: false,
                            loading: false,
                            done: true,
                            error: "" + err,
                            notifications: [],
                            load: state.load,
                            reset: state.reset,
                        });
                    }
                    reject(err);
                    return;
                }
            }, "/notifications", payload, auth.bearer, cacheBusterStore, logger);
        }
        catch (err) {
            logger.error(LOGPREFIX + err);
            if (callback) {
                callback.handleUpdate({
                    ok: false,
                    loading: false,
                    done: true,
                    error: "" + err,
                    notifications: [],
                    load: state.load,
                    reset: state.reset,
                });
            }
            reject(err);
            return;
        }
    });
});
export const useNotificationsStore = create()((set, get) => ({
    ok: false,
    loading: false,
    done: false,
    error: undefined,
    clientDetailCode: undefined,
    notifications: [],
    load: (auth, cacheBusterStore, callback, logger) => __awaiter(void 0, void 0, void 0, function* () {
        set({ loading: true });
        const state = get();
        queryNotifications(state, auth, cacheBusterStore, callback, logger)
            .then((response) => {
            set({
                loading: false,
                ok: true,
                notifications: response.records,
                error: undefined,
                done: true,
            });
        })
            .catch((err) => {
            set({
                loading: false,
                ok: false,
                notifications: [],
                error: "" + err,
                done: true,
            });
        });
    }),
    reset: () => {
        set({
            ok: false,
            loading: false,
            done: false,
            error: undefined,
            clientDetailCode: undefined,
            notifications: [],
        });
    },
}));
const LOCAL_STORAGE_KEY = "lastSeenNotificationTime";
const PERMISSION_REQUESTED_KEY = "notificationPermissionRequested";
export const setLastSeenNotificationTime = (notificationCreatedAt) => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "" + notificationCreatedAt);
};
export const getLastSeenNotificationTime = (logger) => {
    try {
        const lastSeen = Number(localStorage.getItem(LOCAL_STORAGE_KEY));
        return lastSeen;
    }
    catch (err) {
        logger.error("Last seen notification timestamp is invalid - " + err);
        return 0;
    }
};
export const hasRequestedNotificationPermission = () => {
    return localStorage.getItem(PERMISSION_REQUESTED_KEY) === "true";
};
export const setNotificationPermissionRequested = () => {
    localStorage.setItem(PERMISSION_REQUESTED_KEY, "true");
};
export const newNotificationsCount = (state, logger) => {
    if (!state.ok) {
        return 0;
    }
    if (!state.notifications || state.notifications.length <= 0) {
        return 0;
    }
    const lastSeen = getLastSeenNotificationTime(logger);
    const unseen = [];
    state.notifications.forEach((entry) => {
        if (!entry.createdAt) {
            return;
        }
        if (entry.createdAt > lastSeen) {
            unseen.push(entry);
        }
    });
    return unseen.length;
};
export const checkNotificationsCompatible = (logger) => {
    if (!("serviceWorker" in navigator)) {
        logger.error(LOGPREFIX + "No support for service worker!");
        return false;
    }
    if (!("Notification" in window)) {
        logger.error(LOGPREFIX + "No support for notification API");
        return false;
    }
    if (!("PushManager" in window)) {
        logger.error(LOGPREFIX + "No support for Push API");
        return false;
    }
    return true;
};
export const requestNotificationPermission = (logger) => __awaiter(void 0, void 0, void 0, function* () {
    if (!checkNotificationsCompatible(logger)) {
        return false;
    }
    // Check current permission state first
    if (Notification.permission === "granted") {
        logger.log(LOGPREFIX + "Notification permissions already granted.");
        setNotificationPermissionRequested();
        return true;
    }
    if (Notification.permission === "denied") {
        logger.log(LOGPREFIX + "Notification permissions previously denied.");
        setNotificationPermissionRequested();
        return false;
    }
    // Check if we've already asked the user in this browser session
    if (hasRequestedNotificationPermission()) {
        logger.log(LOGPREFIX + "Notification permission already requested in this session.");
        return false;
    }
    // Only request permission if it's currently "default" (not yet asked)
    if (Notification.permission === "default") {
        logger.log(LOGPREFIX + "Requesting notification permission...");
        setNotificationPermissionRequested();
        const permission = yield Notification.requestPermission();
        if (permission !== "granted") {
            logger.log(LOGPREFIX + "Notification permission not granted");
            return false;
        }
        else {
            logger.log(LOGPREFIX + "Notification permissions granted.");
            return true;
        }
    }
    return false;
});
